import React, { useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BackToTop from "../components/BackToTop";
import "../css/HeavyEngineeringPage.css";

// Slideshow component with auto‑play
const TabSlideshow = ({ images, captions, isActive }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isActive && images.length > 1) {
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 5000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isActive, images.length]);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
    resetInterval();
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    resetInterval();
  };

  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      if (isActive && images.length > 1) {
        intervalRef.current = setInterval(() => {
          setCurrentIndex((prev) => (prev + 1) % images.length);
        }, 5000);
      }
    }
  };

  if (!images.length) return null;

  return (
    <div className="slideshow-container">
      <button className="slideshow-arrow arrow-left" onClick={prevSlide}>
        ❮
      </button>
      <button className="slideshow-arrow arrow-right" onClick={nextSlide}>
        ❯
      </button>
      {images.map((img, idx) => (
        <div
          key={idx}
          className={`slide ${idx === currentIndex ? "active" : ""}`}
        >
          <img src={img} alt={captions[idx] || `Slide ${idx + 1}`} />
          {captions[idx] && <div className="caption">{captions[idx]}</div>}
        </div>
      ))}
    </div>
  );
};

const HeavyEngineeringPage = () => {
  const [activeTab, setActiveTab] = useState("tab1");

  // Tab data
  const tabs = [
    {
      id: "tab1",
      label: "Underwater Restaurants/Structures",
      images: [
        "/wp-content/uploads/heavy-engineering/UW1.png",
        "/wp-content/uploads/heavy-engineering/UW2.png",
        "/wp-content/uploads/heavy-engineering/UW3.jpg",
        "/wp-content/uploads/heavy-engineering/UW4.jpg",
      ],
      captions: [
        "You & Me Underwater Restaurant - Uthuru Maafaru Island",
        "OBLU Under Ocean Restaurant - Anugandu Falhu Island",
        "Underwater Gallery - Dhiffushi Island",
        "Underwater Restaurant - Meyyafushi Island",
      ],
      content: (
        <>
          <h3>Pioneering Underwater Excellence in the Maldives</h3>
          <p>
            Colombo Dockyard PLC proudly brings engineering brilliance beneath
            the waves with the successful design and construction of four
            world‑class underwater structures—three restaurants and a
            gallery—crafted for the luxury tourism market in the Maldives.
          </p>
          <p>
            These architectural marvels offer panoramic underwater views through
            high‑grade acrylic domes and walls, blending cutting‑edge maritime
            engineering with sustainable building practices.
          </p>
          <p>
            <strong>Our Underwater Projects:</strong>
          </p>
          <ul>
            <li>
              <span className="location-year">
                You & Me Underwater Restaurant (20 pax)
              </span>{" "}
              – Uthuru Maafaru Island | 2018
            </li>
            <li>
              <span className="location-year">
                OBLU Under Ocean Restaurant (50 pax)
              </span>{" "}
              – Anugandu Falhu Island | 2022
            </li>
            <li>
              <span className="location-year">Underwater Gallery</span> –
              Dhiffushi Island | 2025
            </li>
            <li>
              <span className="location-year">
                Underwater Restaurant (12 pax)
              </span>{" "}
              – Meyyafushi Island | 2025
            </li>
          </ul>
        </>
      ),
    },
    {
      id: "tab2",
      label: "Port and Port Related Infrastructure",
      images: [
        "/wp-content/uploads/heavy-engineering/Port1.jpg",
        "/wp-content/uploads/heavy-engineering/Port2.jpg",
        "/wp-content/uploads/heavy-engineering/Port3.jpg",
      ],
      captions: [
        "Slipway Facility – Port of Bujumbura, Burundi",
        "Reefer Platforms – South Asia Gateway Terminals (SAGT), Colombo",
        "Fender System Repairs",
      ],
      content: (
        <>
          <h3>
            Port Infrastructure Excellence: Delivering Global Marine Solutions
          </h3>
          <p>
            Colombo Dockyard PLC is a trusted name in delivering robust,
            high‑quality port and marine infrastructure solutions worldwide.
          </p>
          <p>
            <strong>Key Projects:</strong>
          </p>
          <ul>
            <li>
              <span className="location-year">
                Slipway Facility – Port of Bujumbura, Burundi
              </span>
              <br />A landmark inland port project on Lake Tanganyika, including
              design, fabrication, and commissioning of a complete slipway
              system.
            </li>
            <li>
              <span className="location-year">
                Reefer Platforms – South Asia Gateway Terminals (SAGT), Colombo
              </span>
              <br />
              Purpose‑built platforms to support refrigerated containers,
              enhancing Colombo Port’s cold chain logistics.
            </li>
            <li>
              <span className="location-year">
                Fender System Repairs – Colombo International Container Terminal
                (CICT)
              </span>
              <br />
              Comprehensive inspection, repair, and reinforcement of fender
              systems.
            </li>
          </ul>
        </>
      ),
    },
    {
      id: "tab3",
      label: "Heavy Engineering Special Projects",
      images: [
        "/wp-content/uploads/heavy-engineering/HE1.jpg",
        "/wp-content/uploads/heavy-engineering/HE2.jpg",
        "/wp-content/uploads/heavy-engineering/HE3.jpg",
        "/wp-content/uploads/heavy-engineering/HE4.jpg",
      ],
      captions: [
        "Heavy Engineering Special Project 1",
        "Heavy Engineering Special Project 2",
        "Heavy Engineering Special Project 3",
        "Heavy Engineering Special Project 4",
      ],
      content: (
        <>
          <h3>
            Heavy Engineering Special Projects: Expertise in Complex Solutions
          </h3>
          <p>
            Colombo Dockyard specializes in delivering innovative and robust
            solutions across a wide range of heavy engineering special projects.
          </p>
          <ul>
            <li>Fuel storage systems</li>
            <li>LPG storage systems</li>
            <li>Cement silos</li>
            <li>
              Piping systems (Oil, LPG, Fire Fighting, and Offshore facilities)
            </li>
            <li>
              Supplying ancillary services for the installation of storage
              systems and pipe systems
            </li>
            <li>
              Installation of power stations (Thermal power stations, Mini Hydro
              power stations)
            </li>
            <li>Main engine (modification and repair work)</li>
            <li>Fabrication and installation of heavy steel structures</li>
            <li>
              Installation of heavy structures related to irrigation and other
              industries
            </li>
            <li>Proof Load Testing and Certification of lifting equipment</li>
          </ul>
        </>
      ),
    },
    {
      id: "tab4",
      label: "Local Heavy Engineering Projects (DGES)",
      images: [
        "/wp-content/uploads/heavy-engineering/DGES1.jpg",
        "/wp-content/uploads/heavy-engineering/DGES2.jpg",
        "/wp-content/uploads/heavy-engineering/DGES3.jpg",
      ],
      captions: ["DGES Project 1", "DGES Project 2", "DGES Project 3"],
      content: (
        <>
          <h3>Local Heavy Engineering Projects (DGES)</h3>
          <p>
            Dockyard General Engineering Services (Pvt) Ltd (DGES) is a
            subsidiary of Colombo Dockyard PLC, one of Sri Lanka’s leading
            shipbuilding and heavy engineering companies.
          </p>
          <p>
            For more information, visit:{" "}
            <a
              href="https://dges.lk/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://dges.lk/
            </a>
          </p>
          <a href="https://dges.lk/" target="_blank" rel="noopener noreferrer">
            <img
              src="/wp-content/uploads/heavy-engineering/DGES Logo.png"
              alt="Dockyard General Engineering Services Logo"
              style={{ maxWidth: "200px", height: "auto" }}
            />
          </a>
        </>
      ),
    },
  ];

  return (
    <>
      <Header />
      <section
        className="banner-header1"
        style={{
          backgroundImage: "url('/wp-content/uploads/2016/02/Gas-Sphere1.jpg')",
        }}
      ></section>
      <section className="he-contentwrapper clearfix he-page-container">
        <div className="he-content clearfix">
          <div className="main-description">
            <h1>Heavy Engineering</h1>
            <h2>25+ Years of Precision, Innovation & Impact</h2>
            <p>
              For over 25 years, Colombo Dockyard PLC’s Heavy Engineering
              Department has stood at the forefront of engineering
              excellence—epitomizing innovation, precision craftsmanship, and
              unwavering commitment in the design, fabrication, and installation
              of complex underwater and heavy steel structures.
            </p>
            <p>
              With a legacy rooted in technical mastery and visionary thinking,
              our department has played a pivotal role in transforming both Sri
              Lanka’s industrial landscape and making its mark on the global
              engineering stage.
            </p>
          </div>
          <div className="tabs">
            <div className="tab-buttons">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            {tabs.map((tab) => (
              <div
                key={tab.id}
                className={`tab-content ${activeTab === tab.id ? "active" : ""}`}
              >
                <div className="tab-content-inner">
                  <div className="tab-text">{tab.content}</div>
                  <div className="tab-slideshow">
                    <TabSlideshow
                      images={tab.images}
                      captions={tab.captions}
                      isActive={activeTab === tab.id}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <Footer />
      <BackToTop />
    </>
  );
};

export default HeavyEngineeringPage;
